// ─────────────────────────────────────────────────────────────
// ShipPendingService — 배송처리 pending 시리얼 서버 저장
// v7.149: 다중 PC 공유를 위한 Supabase 테이블 (ship_pending_serials)
// 기존 localStorage 저장은 오프라인/장애 폴백으로 유지
//
// 스키마 (수동 SQL 필요, 아직 없으면 v7.149 배포 후 SQL Editor에서 실행):
// CREATE TABLE ship_pending_serials (
//   req_id TEXT NOT NULL,
//   serial TEXT NOT NULL,
//   model TEXT NOT NULL,
//   model_log_id BIGINT,
//   external BOOLEAN DEFAULT false,
//   serial_optional BOOLEAN DEFAULT false,
//   created_at TIMESTAMPTZ DEFAULT NOW(),
//   updated_at TIMESTAMPTZ DEFAULT NOW(),
//   PRIMARY KEY (req_id, serial)
// );
// CREATE INDEX idx_ship_pending_req ON ship_pending_serials(req_id);
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

// 서버 row → 클라이언트 형식 변환
const rowToItem = (row) => ({
  serial: row.serial,
  model: row.model,
  modelLogId: row.model_log_id,
  external: !!row.external,
  serialOptional: !!row.serial_optional,
});

// 클라이언트 item → 서버 row 변환
const itemToRow = (reqId, item) => ({
  req_id: reqId,
  serial: item.serial,
  model: item.model,
  model_log_id: item.modelLogId != null ? item.modelLogId : null,
  external: !!item.external,
  serial_optional: !!item.serialOptional,
  updated_at: new Date().toISOString(),
});

export const ShipPendingService = {
  // v7.434: 여러 요청의 pending 을 한 번에 조회 → { [req_id]: { [model]: count } }
  //   배송시트지 [재고부족] 판정용. v7.424 부터 담은 시리얼은 inventory 가 ship_hold 로 빠지므로
  //   in_stock 카운트만 보면 '자기가 이미 담아둔 수량' 이 가용에서 누락돼 부족으로 오판된다.
  async countByReqIds(reqIds) {
    const ids = [...new Set((reqIds || []).filter(Boolean))];
    if (ids.length === 0) return {};
    const { data, error } = await supabase.from('ship_pending_serials')
      .select('req_id, model').in('req_id', ids);
    if (error || !data) return {};
    const out = {};
    data.forEach(r => {
      if (!r.req_id) return;
      if (!out[r.req_id]) out[r.req_id] = {};
      const m = r.model || '';
      out[r.req_id][m] = (out[r.req_id][m] || 0) + 1;
    });
    return out;
  },

  // 특정 요청의 pending 시리얼 전체 조회
  async loadByReqId(reqId) {
    if (!reqId) return { ok: false, error: 'no reqId', data: [] };
    const { data, error } = await supabase
      .from('ship_pending_serials')
      .select('*')
      .eq('req_id', reqId)
      .order('created_at', { ascending: true }); // v7.196: 추가 순서 보장 (수량 감소 시 최근분부터 해제)
    if (error) return { ok: false, error, data: [] };
    return { ok: true, data: (data || []).map(rowToItem) };
  },

  // 시리얼 1건 추가 (upsert — 같은 req_id+serial이면 갱신)
  async addSerial(reqId, item) {
    if (!reqId || !item?.serial) return { ok: false, error: 'invalid args' };
    const { error } = await supabase
      .from('ship_pending_serials')
      .upsert(itemToRow(reqId, item), { onConflict: 'req_id,serial' });
    return { ok: !error, error };
  },

  // 시리얼 여러 건 추가 (autoExtPending / assignExternalToCurrentModel 용)
  async addSerials(reqId, items) {
    if (!reqId || !Array.isArray(items) || items.length === 0) {
      return { ok: true, error: null };
    }
    const rows = items.map(item => itemToRow(reqId, item));
    const { error } = await supabase
      .from('ship_pending_serials')
      .upsert(rows, { onConflict: 'req_id,serial' });
    return { ok: !error, error };
  },

  // 특정 시리얼 1건 삭제
  async removeSerial(reqId, serial) {
    if (!reqId || !serial) return { ok: false, error: 'invalid args' };
    const { error } = await supabase
      .from('ship_pending_serials')
      .delete()
      .eq('req_id', reqId)
      .eq('serial', serial);
    return { ok: !error, error };
  },

  // 특정 요청의 pending 전체 삭제 (커밋 성공 시)
  async clearByReqId(reqId) {
    if (!reqId) return { ok: false, error: 'no reqId' };
    const { error } = await supabase
      .from('ship_pending_serials')
      .delete()
      .eq('req_id', reqId);
    return { ok: !error, error };
  },

  // 7일 초과 stale pending 자동 정리 (앱 시작 시 호출)
  async cleanupStale() {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from('ship_pending_serials')
      .delete()
      .lt('updated_at', sevenDaysAgo);
    return { ok: !error, error };
  },
};
